import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import useCrmMoney from "../hooks/useCrmMoney";
import CrmDialog from "../ui/CrmDialog";
import CrmSelect from "../ui/CrmSelect";
import CrmInput from "../ui/CrmInput";
import CrmButton from "../ui/CrmButton";
import EmptyState from "../ui/EmptyState";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Assign pack" (spec Screen 7): for offline / cash sales — pick a pack, ◉ Mark paid or
 * ○ Record amount, Assign. The pack catalogue is `GET /api/crm/packs` (Phase 2); until
 * the practice has a pack, the dialog says so and points to Packs.
 */
export default function AssignPackDialog({ client, onClose, onAssigned }) {
  const t = useT("crm");
  const tc = useT("common");
  const money = useCrmMoney();
  const [packs, setPacks] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [packId, setPackId] = useState("");
  const [mode, setMode] = useState("marked_paid");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    crmApi.listPacks({ status: "active" })
      .then((page) => { if (alive) setPacks(Array.isArray(page?.rows) ? page.rows : []); })
      .catch((e) => { if (alive) setLoadError(e); });
    return () => { alive = false; };
  }, []);

  const cents = Math.round(Number(String(amount).replace(",", ".")) * 100);
  const ready = packId && (mode === "marked_paid" || (Number.isFinite(cents) && cents >= 0 && amount !== ""));

  const assign = async () => {
    setBusy(true);
    try {
      await crmApi.assignPack(client.id, { pack_id: packId, payment: mode === "marked_paid" ? { mode } : { mode, amount_cents: cents } });
      toast.success(t("assign.done"));
      onAssigned?.();
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} testId={CRM.assignPackDialog}
      title={t("assign.title", { name: client.first_name || client.display_name })}
      footer={packs && packs.length > 0 && (
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={assign} disabled={!ready || busy} data-testid={CRM.assignPackSubmit}>{t("assign.submit")}</CrmButton>
        </>
      )}>
      {loadError && <CrmLoadFailure error={loadError} />}
      {packs && packs.length === 0 && <EmptyState testId={CRM.assignPackEmpty} title={t("assign.empty")} body={t("assign.emptyBody")} />}
      {packs && packs.length > 0 && (
        <div className="space-y-4">
          <CrmSelect className="w-full" selectClassName="w-full" value={packId} onChange={(e) => setPackId(e.target.value)} placeholder={t("assign.choose")}
            aria-label={t("assign.pack")} options={packs.map((p) => ({ value: p.id, label: `${p.name} · ${money(p.price_cents, p.currency)}` }))}
            data-testid={CRM.assignPackSelect} />
          <fieldset className="flex flex-wrap gap-4 text-sm">
            <legend className="sr-only">{t("assign.payment")}</legend>
            {["marked_paid", "recorded_amount"].map((m) => (
              <label key={m} className="inline-flex items-center gap-2">
                <input type="radio" name="crm-assign-mode" checked={mode === m} onChange={() => setMode(m)} className="h-4 w-4 accent-eh-sage-deep" />
                {t(m === "marked_paid" ? "assign.markPaid" : "assign.recordAmount")}
              </label>
            ))}
          </fieldset>
          {mode === "recorded_amount" && (
            <div>
              <label htmlFor="crm-assign-amount" className={LABEL}>{t("assign.amount")}</label>
              <CrmInput id="crm-assign-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          )}
        </div>
      )}
    </CrmDialog>
  );
}
